import { cloneElement, PropsWithChildren, ReactElement, RefObject, useEffect, useState } from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal, useClick, useDismiss,
  useFloating,
  useInteractions, useMergeRefs
} from "@floating-ui/react";
import { Icon, IconX } from "@tabler/icons-react";
import { useMediaQueryDown } from "../../../hooks/media-queries";
import "./stickable-bottom.scss";
import { classNames } from "@ct-react/core";

type ValidChildren = [ ReactElement<any, typeof Content>, ReactElement<any, typeof Resume> ];
type Props = {
  activator?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  children: ValidChildren;
}

export const Resume = ({ className, icon: Icon, children }: PropsWithChildren & { className?: string, icon: Icon, asFloatingRef?: boolean }) => (
  <>
    <div><Icon className="s-icon"/></div>
    <div className={classNames("resume", className)}>{children}</div>
  </>);

export const Content = ({ children }: PropsWithChildren & { className?: string, title?: string }) => children;

const StickableBottom = ({ activator, open, setOpen, children }: Props) => {

  const [ content, resume ] = children;

  const { context, refs } = useFloating({
    open,
    onOpenChange: setOpen,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    ...(!!resume.props.asFloatingRef) ? [ useClick(context) ] : [],
    useDismiss(context, { escapeKey: true })
  ]);

  const isDisplayUntilTablet = useMediaQueryDown("tablet");
  const isDisplayMobileLandscape = useMediaQueryDown("desktop", { orientation: "landscape" });
  const isSticked = typeof activator === "boolean" ? activator : isDisplayUntilTablet || isDisplayMobileLandscape;

  const wrapperClasses = classNames("overlay-content", "sticked-aside");
  const bodyClasses = classNames("sticked-aside-body", content.props.className);

  if (!isSticked)
    return content;

  return (
    <>
      <div className="stickable opener"
           {...(!!resume.props.asFloatingRef) && { ref: refs.setReference, ...getReferenceProps() }}>
         {resume}
      </div>
      {open &&
        <FloatingPortal id="article-aside">
          <FloatingOverlay className="overlay-bg" lockScroll />
          <FloatingFocusManager context={context} initialFocus={-1}>
            <div className={wrapperClasses}
                 ref={refs.setFloating}
                 {...getFloatingProps()}>
              <IconX className="c-icon" onClick={() => setOpen(false)} />
              {!!content.props.title &&
                <div className="sticked-aside-header">
                  <h1>{content.props.title}</h1>
                </div>
              }
              <div className={bodyClasses}>{content}</div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      }
    </>);

};

export default StickableBottom;
